import {
  InstrumentStoreItem,
  Inventory,
  ListedInstrument,
  Loadable,
  LoadableState,
  ObjectSet,
  updateInventoryMarketPrice,
  useCurrencyExchangeRate,
  useSubscribeToInstrumentChanges,
} from '@eih-netbroker/common';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Config } from '../../../shared/config/config';
import { useInventory } from '../../../shared/services/inventory/useInventory';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { useLogger } from '../../../shared/utils/useLogger';

const realtimeInventoriesAtom = atom<LoadableState<Array<Inventory>>>(Loadable.uninitialized);

export const RealtimeInventoryProvider = ({ children }: { children: React.ReactNode }) => {
  const { inventoryDetails } = useInventory();
  const [inventoryList, setInventoryList] = useState<Array<ListedInstrument>>([]);
  const [realtimeInventories, setRealtimeInventories] = useAtom(realtimeInventoriesAtom);
  const { subscribeToInstrumentChanges } = useEStream();

  const { getCurrenciesExchangeRateAsMap } = useCurrencyExchangeRate();

  const logger = useLogger('realtimeInventory');

  const handleChanges = (changes: ObjectSet<InstrumentStoreItem>) => {
    let hasChanges = false;
    if (realtimeInventories.isLoaded) {
      const newTradableInventories = realtimeInventories.map(inventory => {
        if (!inventory.ric) {
          return inventory;
        }

        const changedItem = changes.get(inventory.ric);
        if (changedItem && changedItem.price && changedItem.ccy) {
          const newTradableInventory = { ...inventory };

          updateInventoryMarketPrice({
            inventory: newTradableInventory,
            logger,
            exchangeRates: getCurrenciesExchangeRateAsMap(),
            config: Config,
            latestPriceCcy: changedItem.ccy,
            latestPrice: changedItem.price.value,
          });

          hasChanges = true;
          return newTradableInventory;
        }

        return inventory;
      }, []);

      if (hasChanges) {
        setRealtimeInventories(Loadable.loaded(newTradableInventories));
      }
    }
  };

  useSubscribeToInstrumentChanges(
    {
      source: `inventoryContext`,
      instrument: inventoryList,
      onChange: handleChanges,
      subscribeToInstrumentChanges,
    },
    [inventoryList],
  );

  useEffect(() => {
    if (inventoryDetails.isLoaded) {
      const inventoriesRic = inventoryDetails.tradableInventories?.filter(i => i.ric).map(i => i.instrument);
      setInventoryList(inventoriesRic ?? []);
    }
  }, [inventoryDetails]);

  useEffect(() => {
    setRealtimeInventories(Loadable.map(inventoryDetails, ({ inventories }) => inventories));
  }, [inventoryDetails, setRealtimeInventories]);

  return <>{children}</>;
};

export const useRealtimeInventories = () => {
  const realtimeInventories = useAtomValue(realtimeInventoriesAtom);
  return { realtimeInventories };
};
